import React, { useState, useEffect } from 'react';
import CurrencyInput from '../CurrencyInput';
import FavorecidoPicker from './FavorecidoPicker';
import TipoBemPicker from './TipoBemPicker';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import { formatNfeChave } from '../masks/formatNfeChave';

function isChaveNfeValida(chave) {
  if (!/^\d{44}$/.test(chave)) return false;
  // Cálculo do dígito verificador (módulo 11)
  let soma = 0;
  let peso = 2;
  for (let i = 42; i >= 0; i--) {
    soma += parseInt(chave[i], 10) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  let dv = 11 - resto;
  if (dv >= 10) dv = 0;
  return dv === parseInt(chave[43], 10);
}

function DespesaModal({
  show, onClose, onSubmit, form, handleChange,
  programas = [], acoes = [], naturezas = [], tiposBens = [], favorecidos = [],
  title = 'CADASTRO DE DESPESA'
}) {
  // Handler especial para pgNum: só aceita números se for CHEQUE
  function handlePgNumChange(e) {
    let value = e.target.value;
    if (form.pgForma === 'CHEQUE') {
      // Aceita apenas números
      value = value.replace(/[^\d]/g, '');
    }
    handleChange({ target: { name: 'pgNum', value } });
  }
  // Estado para controlar label e bloqueio do campo pgNum
  const [pgNumLabel, setPgNumLabel] = useState('Descrição Pagamento');
  const [pgNumDisabled, setPgNumDisabled] = useState(false);

  // Atualiza label e valor de pgNum conforme forma de pagamento
  useEffect(() => {
    if (!show) return;
    switch (form.pgForma) {
      case 'CHEQUE':
        setPgNumLabel('Nº do Cheque');
        setPgNumDisabled(false);
        // Limpar pgNum se tiver um valor pré-preenchido de outro tipo
        if (form.pgNum === 'DÉBITO' || form.pgNum === 'DINHEIRO' || form.pgNum === 'PIX' || form.pgNum === 'TRANSFERÊNCIA') {
          handleChange({ target: { name: 'pgNum', value: '' } });
        }
        break;
      case 'DÉBITO CARTÃO':
        setPgNumLabel('Descrição Pagamento');
        handleChange({ target: { name: 'pgNum', value: 'DÉBITO' } });
        setPgNumDisabled(true);
        break;
      case 'DINHEIRO':
        setPgNumLabel('Descrição Pagamento');
        handleChange({ target: { name: 'pgNum', value: 'DINHEIRO' } });
        setPgNumDisabled(true);
        break;
      case 'PIX':
        setPgNumLabel('Descrição Pagamento');
        handleChange({ target: { name: 'pgNum', value: 'PIX' } });
        setPgNumDisabled(true);
        break;
      case 'TRANSF. BANCÁRIA':
        setPgNumLabel('Descrição Pagamento');
        handleChange({ target: { name: 'pgNum', value: 'TRANSFERÊNCIA' } });
        setPgNumDisabled(true);
        break;
      default:
        setPgNumLabel('Descrição Pagamento');
        setPgNumDisabled(false);
    }
  }, [form.pgForma, show]);

  // Estado para controlar label do campo docNum
  const [docNumLabel, setDocNumLabel] = useState('Nº Documento');

  // Atualiza label de docNum conforme tipo de documento
  useEffect(() => {
    if (!show) return;
    switch (form.docTipo) {
      case 'NOTA FISCAL':
        setDocNumLabel('Nº da Nota Fiscal');
        break;
      case 'RECIBO':
        setDocNumLabel('Nº do Recibo');
        break;
      default:
        setDocNumLabel('Nº Documento');
    }
  }, [form.docTipo, show]);

  // Verifica se programa é PDDE QUALIDADE ou PDDE EQUIDADE e seta pgForma para PIX
  useEffect(() => {
    if (form.idPrograma) {
      const programaSelecionado = programas.find(p => String(p.id) === String(form.idPrograma));
      if (programaSelecionado) {
        const descricao = programaSelecionado.descricao.toUpperCase();
        if (descricao.includes('PDDE QUALIDADE') || descricao.includes('PDDE EQUIDADE')) {
          if (form.pgForma !== 'PIX') {
            handleChange({ target: { name: 'pgForma', value: 'PIX' } });
          }
        }
      }
    }
  }, [form.idPrograma]);

  // Validação: data de pagamento não pode ser menor que data do documento
  const dataDoc = form.docData ? new Date(form.docData) : null;
  const dataPg = form.pgData ? new Date(form.pgData) : null;
  const pgMenorQueDoc = dataDoc && dataPg && dataPg < dataDoc;
  // Função para preencher valores padrão ao abrir o modal
  useEffect(() => {
    if (show) {
      // Seta Origem padrão
      if (!form.origem) {
        handleChange({ target: { name: 'origem', value: 'FNDE' } });
      }
      // Seta Tipo Documento padrão
      if (!form.docTipo) {
        handleChange({ target: { name: 'docTipo', value: 'NOTA FISCAL' } });
      }
      // Seta a data padrão no formato yyyy-MM-dd usando o ano do localStorage e mês/dia atuais
      const ano = localStorage.getItem('ano');
      if (ano) {
        const now = new Date();
        const mes = String(now.getMonth() + 1).padStart(2, '0');
        const dia = String(now.getDate()).padStart(2, '0');
        const dataPadrao = `${ano}-${mes}-${dia}`;
        // docData
        if (!form.docData) {
          handleChange({ target: { name: 'docData', value: dataPadrao } });
        }
        // pgData
        if (!form.pgData) {
          handleChange({ target: { name: 'pgData', value: dataPadrao } });
        }
      }
    }
  }, [show]);
  const [showPicker, setShowPicker] = useState(false);
  const [contas, setContas] = useState([]);
  const [showTipoBemPicker, setShowTipoBemPicker] = useState(false);
  const isEditing = form._isEditing;

  useEffect(() => {
    if (show && form.idPrograma) {
      const acoesDisponiveis = acoes.filter(a => a.idPrograma === parseInt(form.idPrograma));
      if (acoesDisponiveis.length === 1 && !form.idAcao) {
        handleChange({ target: { name: 'idAcao', value: acoesDisponiveis[0].id } });
      }
    }
  }, [show, form.idPrograma, acoes.length]);

  useEffect(() => {
    if (show && form.idPrograma && contas.length > 0) {
      const contasDisponiveis = contas.filter(c => String(c.idPrograma) === String(form.idPrograma) && c.debitar === 'SIM');
      if (contasDisponiveis.length === 1 && !form.idConta) {
        handleChange({ target: { name: 'idConta', value: contasDisponiveis[0].numConta } });
      }
    }
  }, [show, form.idPrograma, contas.length]);

  useEffect(() => {
    if (show && form.idPrograma && form.idEscola) {
      axios.get(`${API_BASE_URL.CONTA}/findByEscolaDebitarSim`, {
        params: { idEscola: form.idEscola },
        headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined }
      }).then(r => setContas(r.data)).catch(() => setContas([]));
    }
  }, [show, form.idPrograma, form.idEscola]);

  const selectedFavorecido = favorecidos.find(f => f.cpfOrCnpj === form.idFavorecido);

  const anoBase = localStorage.getItem('ano');

  const anoDocData = form.docData ? new Date(form.docData + 'T00:00:00').getFullYear().toString() : null;
  const anoPgData  = form.pgData  ? new Date(form.pgData  + 'T00:00:00').getFullYear().toString() : null;

  const docDataInvalida = anoDocData && anoBase && anoDocData !== anoBase;
  const pgDataInvalida  = anoPgData  && anoBase && anoPgData  !== anoBase;

  function handleSubmit(e) {
    e.preventDefault();
    const chaveNfInvalida = form.chaveNf && form.chaveNf.length > 0 && !isChaveNfeValida(form.chaveNf);
    if (docDataInvalida || pgDataInvalida || pgMenorQueDoc || chaveNfInvalida) return;
    // Remover máscara (espaços) antes de enviar
    const formToSend = { ...form, chaveNf: form.chaveNf ? form.chaveNf.replace(/\D/g, '') : '' };
    onSubmit(e, formToSend);
  }

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', inset: 0, overflowY: 'auto' }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxHeight: 'calc(100vh - 2rem)', margin: '1rem auto' }}>
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header bg-success text-white" style={{ padding: '0.35rem 1rem' }}>
            <h6 className="modal-title mb-0 small">
              <i className="bi bi-receipt me-2"></i>{title}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={() => { setShowPicker(false); onClose(); }}></button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ overflowY: 'auto' }}>
            <form id="despesa-form" onSubmit={handleSubmit} noValidate>

              {/* Seção: Classificação */}
              <div className="border rounded p-2 mb-2">
                <p className="text-muted small fw-semibold mb-2"><i className="bi bi-diagram-3 me-1"></i>CLASSIFICAÇÃO</p>
                <div className="row g-3 mb-2">
                  <div className="col-md-4">
                    <label className="form-label">Programa *</label>
                    <select className="form-select form-select-sm" name="idPrograma" value={form.idPrograma} onChange={handleChange} required disabled={isEditing}>
                      <option value="">Selecione...</option>
                      {programas.map(p => <option key={p.id} value={String(p.id)}>{p.descricao}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Ação *</label>
                    <select className="form-select form-select-sm" name="idAcao" value={form.idAcao} onChange={handleChange} required disabled={!form.idPrograma || isEditing}>
                      <option value="">{form.idPrograma ? 'Selecione...' : 'Selecione um programa'}</option>
                      {acoes.filter(a => String(a.idPrograma) === String(form.idPrograma))
                        .map(a => <option key={a.id} value={String(a.id)}>{a.descricao}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Conta *</label>
                    <select className="form-select form-select-sm" name="idConta" value={form.idConta} onChange={handleChange} required disabled={!form.idPrograma || isEditing}>
                      <option value="">{form.idPrograma ? 'Selecione...' : 'Selecione um programa'}</option>
                      {contas.filter(c => String(c.idPrograma) === String(form.idPrograma) && c.debitar === 'SIM')
                        .map(c => <option key={c.numConta} value={c.numConta}>{c.numConta}</option>)}
                    </select>
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label">Favorecido *</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={selectedFavorecido ? `${selectedFavorecido.nome} (${selectedFavorecido.cpfOrCnpj})` : ''}
                        placeholder="Selecione um favorecido..."
                        readOnly
                        required
                        style={{ cursor: 'pointer', backgroundColor: '#fff' }}
                        onClick={() => setShowPicker(true)}
                      />
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPicker(true)} title="Escolher favorecido">
                        <i className="bi bi-search" />
                      </button>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Natureza *</label>
                    <select className="form-select form-select-sm" name="idNatureza" value={form.idNatureza} onChange={handleChange} required>
                      <option value="">Selecione...</option>
                      {naturezas.map(n => <option key={n.id} value={n.id}>{n.descricao}</option>)}
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Tipo Bem *</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={(() => {
                          const tipo = tiposBens.find(t => t.id === form.idTipoBem && t.idNatureza === form.idNatureza);
                          return tipo ? tipo.descricao : '';
                        })()}
                        placeholder="Selecione o tipo de bem..."
                        readOnly
                        required
                        style={{ cursor: form.idNatureza ? 'pointer' : 'not-allowed', backgroundColor: form.idNatureza ? '#fff' : '#eee' }}
                        onClick={() => form.idNatureza && setShowTipoBemPicker(true)}
                        disabled={!form.idNatureza}
                      />
                      <button type="button" className="btn btn-outline-secondary" onClick={() => form.idNatureza && setShowTipoBemPicker(true)} title="Escolher tipo de bem" disabled={!form.idNatureza}>
                        <i className="bi bi-search" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <TipoBemPicker
                show={showTipoBemPicker}
                tiposBens={tiposBens.filter(t => !form.idNatureza || t.idNatureza === form.idNatureza)}
                selectedTipoBem={form.idTipoBem}
                onSelect={t => {
                  handleChange({ target: { name: 'idTipoBem', value: t.id } });
                  setShowTipoBemPicker(false);
                }}
                onClose={() => setShowTipoBemPicker(false)}
              />

              {/* Seção: Documento */}
              <div className="border rounded p-2 mb-2">
                <p className="text-muted small fw-semibold mb-2"><i className="bi bi-file-earmark-text me-1"></i>DOCUMENTO</p>
                <div className="row g-3">
                  <div className="col-md-2">
                    <label className="form-label">Tipo Documento *</label>
                    <select className="form-select form-select-sm" name="docTipo" value={form.docTipo} onChange={handleChange} required>
                      <option value="">Selecione...</option>
                      <option value="NOTA FISCAL">NOTA FISCAL</option>
                      <option value="RECIBO">RECIBO</option>
                      <option value="OUTROS">OUTROS</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">{docNumLabel} *</label>
                    <input type="text" className="form-control form-control-sm" name="docNum" value={form.docNum} onChange={handleChange} required />
                  </div>
                  {/* CHAVE_NF: campo editável e validável */}
                  <div className="col-md-5">
                    <label className="form-label">Chave NF-e</label>
                    <input
                      type="text"
                      className={`form-control form-control-sm${form.chaveNf && !isChaveNfeValida(form.chaveNf) ? ' is-invalid' : ''}`}
                      name="chaveNf"
                      value={formatNfeChave(form.chaveNf || '')}
                      onChange={e => {
                        // Limita a 44 caracteres e só permite números
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 44) handleChange({ target: { name: 'chaveNf', value: val } });
                      }}
                      maxLength={54} // 44 dígitos + 10 espaços
                      placeholder="44 dígitos da NF-e"
                      title="A chave da NF-e deve conter exatamente 44 dígitos numéricos."
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Data Documento *</label>
                    <input
                      type="date"
                      className={`form-control form-control-sm${docDataInvalida ? ' is-invalid' : ''}`}
                      name="docData"
                      value={form.docData}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Origem *</label>
                    <select className="form-select form-select-sm" name="origem" value={form.origem} onChange={handleChange} required>
                      <option value="">Selecione...</option>
                      <option value="FNDE">FNDE</option>
                      <option value="REC. PRÓPRIO">REC. PRÓPRIO</option>
                      <option value="OUTROS">OUTROS</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Valor *</label>
                    <CurrencyInput className="form-control form-control-sm" name="vlr" value={form.vlr} onChange={handleChange} required />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Forma de Pagamento *</label>
                    <select className="form-select form-select-sm" name="pgForma" value={form.pgForma} onChange={handleChange} required>
                      <option value="">Selecione...</option>
                      <option value="CHEQUE">CHEQUE</option>
                      <option value="DÉBITO CARTÃO">DÉBITO CARTÃO</option>
                      <option value="DINHEIRO">DINHEIRO</option>
                      <option value="PIX">PIX</option>
                      <option value="TRANSF. BANCÁRIA">TRANSF. BANCÁRIA</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">{pgNumLabel} *</label>
                    <input type="text" className="form-control form-control-sm" name="pgNum" value={form.pgNum} onChange={handlePgNumChange} required disabled={pgNumDisabled} />
                  </div>
                   <div className="col-md-2">
                     <label className="form-label">Data Pagamento *</label>
                     <input
                       type="date"
                       className={`form-control form-control-sm${pgDataInvalida ? ' is-invalid' : ''}`}
                       name="pgData"
                       value={form.pgData}
                       onChange={handleChange}
                       required
                     />
                   </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer" style={{ padding: '0.4rem 1rem' }}>
            {(docDataInvalida || pgDataInvalida || pgMenorQueDoc || (form.chaveNf && form.chaveNf.length > 0 && !isChaveNfeValida(form.chaveNf))) && (
              <span className="text-danger small me-auto">
                <i className="bi bi-exclamation-triangle me-1"></i>
                {pgMenorQueDoc
                  ? 'A data de pagamento não pode ser menor que a data do documento.'
                  : docDataInvalida && pgDataInvalida
                  ? `Data do documento e pagamento devem ser do ano ${anoBase}.`
                  : docDataInvalida
                  ? `A data do documento deve ser do ano ${anoBase}.`
                  : pgDataInvalida
                  ? `A data de pagamento deve ser do ano ${anoBase}.`
                  : (form.chaveNf && form.chaveNf.length > 0 && !isChaveNfeValida(form.chaveNf))
                  ? 'A chave da NF-e é inválida (formato ou dígito verificador incorreto).'
                  : ''}
              </span>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowPicker(false); onClose(); }}>Cancelar</button>
            <button type="submit" form="despesa-form" className="btn btn-success btn-sm" disabled={docDataInvalida || pgDataInvalida || pgMenorQueDoc || (form.chaveNf && form.chaveNf.length > 0 && !isChaveNfeValida(form.chaveNf))}>Salvar</button>
          </div>

        </div>
      </div>

      <FavorecidoPicker
        show={showPicker}
        favorecidos={favorecidos}
        selectedCpfOrCnpj={form.idFavorecido}
        onSelect={f => {
          handleChange({ target: { name: 'idFavorecido', value: f.cpfOrCnpj } });
          setShowPicker(false);
        }}
        onClose={() => setShowPicker(false)}
      />
    </div>
  );
}

export default DespesaModal;
